import { isEmpty } from 'lodash-es';
import { i18n } from '@/i18n';
import { useWorkflowManager } from '@/shared/helpers/helpers';

const DEFAULT_GENDER = 'female';

export function useI18n() {
  const template = /[{].*[.].*[}]/g;

  function hasVariables(message: string): boolean {
    return template.test(message) || message.includes('$') || message.includes('_gender');
  }

  function replaceGender(message: string, list?: any): string {
    const parsed = JSON.parse(message);

    if (parsed._gender) {
      const gender = list?.$translationMeta?.gender
        ?? useWorkflowManager().compiler.userInfo?.gender?.toLowerCase()
        ?? DEFAULT_GENDER;

      return parsed._gender[gender] ?? message;
    }

    return message;
  }

  function replaceContext(message: string, contextVariables: any): string {
    Object.entries(contextVariables).forEach(([name, value]) => {
      if (message.includes(name)) {
        message = message.replaceAll(name, `${value}`);
      }
    });

    return message;
  }

  function replaceMatches(message: string, list: any) {
    const variables = message.match(template);

    if (variables?.length > 0) {
      variables.forEach(variable => {
        const cleanVariable = variable.replace('{', '').replace('}', '');

        if (list?.[cleanVariable]) {
          message = message.replaceAll(variable, list[cleanVariable]);
        }
      });
    }

    return message;
  }

  function replacer(message: string, list?: any) {
    if (message.includes('_gender')) {
      return replaceGender(message, list);
    }

    if (list?.contextVariables) {
      message = replaceContext(message, list.contextVariables);
    }

    if (!isEmpty(list)) {
      message = replaceMatches(message, list);
    }

    return message;
  }

  function customT(key: string, list?: any, options?: any): string {
    if (typeof key !== 'string' && typeof key !== 'number') {
      return key;
    }

    const { t, tm } = i18n.global;
    const message: string = tm(key).toString();

    if (hasVariables(message)) {
      return replacer(message, list);
    }

    // TODO the @-symbol is the part of interpolation, it can be here until we don't using it
    // @see https://vue-i18n.intlify.dev/guide/essentials/syntax.html#linked-messages
    if (message.includes('@')) {
      return message;
    }

    return t(key, list, { ...options, escapeParameter: false });
  }

  return { ...i18n.global, t: customT };
}
